/**
 * Column View of the Orders page: the same filtered, sorted list the Live View
 * shows as cards — marketplace, email and legacy-app orders together — as rows.
 * The filters live on the page, so switching views keeps them.
 *
 * Kept from the old table (OrdersData, still used by MerchantOrders): the
 * pagination, pickup-specific status labels ("Ready Pickup", "Picked Up"), a
 * tint on orders still open, the place-order subscription on the restaurant's
 * order list, and keeping an open order's modal in step with fresh data.
 */
import React, { useEffect, useState } from 'react'
import { gql } from '@apollo/client'
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  TableSortLabel, Typography
} from '@mui/material'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'
import { subscribePlaceOrder } from '../../apollo'
import paymentLabel from '../../utils/paymentLabel'
import { dateIn, orderZone, timeIn } from '../../utils/restaurantTime'
import { STATUS_CONFIG, isRefundedOrder, orderTotal } from './OrderCard'
import { COURIER_STATUS_CONFIG } from './EmailOrderCard'
import { EHUNGRY_STATUS_CONFIG, EHUNGRY_TYPE_LABEL } from './EhungryOrderCard'
import EmailSourceChip from './EmailSourceChip'
import { ops, FS, RADIUS, SHADOW, EASE, tabular, labelSx, focusRing, noMotion, shimmer } from './opsTokens'
import { StatusChip, Tag, FulfilmentTag, InitialsAvatar, CateringTag } from './OpsUi'

const ORDER_PLACED = gql`${subscribePlaceOrder}`

const money = (sym, n) => (n == null || isNaN(Number(n)) ? '—' : `${sym}${Number(n).toFixed(2)}`)
const OPEN = o => !['DELIVERED', 'CANCELLED'].includes(o.orderStatus)

/* One row's worth of display values, whatever kind of order it is. */
const toRow = (o, sym) => {
  if (o.__ehungry) {
    const cfg = EHUNGRY_STATUS_CONFIG[o.status] || { label: o.status, tone: 'neutral' }
    // Stored as HTML by the legacy app ("Visa ....5369<br />Name: …"); first line only.
    const pay = String(o.paymentDescription || '').split(/<br\s*\/?>/i)[0].replace(/<[^>]+>/g, '').trim()
    return {
      id: `#${o.remoteId}`,
      source: <Tag icon={<StorefrontOutlinedIcon />}>RMF (old)</Tag>,
      name: o.customerName, phone: o.customerPhone, restaurant: o.restaurantName,
      items: (o.items || []).map(i => `${i.quantity}x ${i.name}${i.variant ? ` [${i.variant}]` : ''}`).join(', '),
      total: money(sym, o.total),
      payment: pay || '—',
      type: <FulfilmentTag kind={{ PICKUP: 'pickup', DINEIN: 'dinein' }[o.orderType] || 'delivery'}>{EHUNGRY_TYPE_LABEL[o.orderType] || o.orderType || 'Order'}</FulfilmentTag>,
      status: <StatusChip size="sm" toneName={cfg.tone} label={cfg.label} />,
      open: o.status === 'pending'
    }
  }
  if (o.__courier) {
    const cfg = COURIER_STATUS_CONFIG[o.status] || { label: o.status, tone: 'neutral' }
    return {
      id: `#${o.externalOrderNumber || o._id}`,
      source: <EmailSourceChip provider={o.merchantProvider} label="Email" />,
      name: o.customerName, phone: o.customerPhone, restaurant: o.merchantName,
      items: (o.items || []).map(i => `${i.quantity}x ${i.title}`).join(', '),
      total: money(sym, o.total),
      // null is unknown (no payment line on the receipt), not the same as unpaid.
      payment: o.isPaid == null ? 'Status unknown' : `${o.isPaid ? 'Paid' : 'Unpaid'}${o.paymentMethod ? ` · ${o.paymentMethod}` : ''}`,
      type: <FulfilmentTag kind={o.orderKind === 'DELIVERY' ? 'delivery' : 'pickup'}>{o.orderKind === 'DELIVERY' ? 'Delivery' : (o.orderKind || 'Unknown')}</FulfilmentTag>,
      status: <StatusChip size="sm" toneName={cfg.tone} label={cfg.label} />,
      open: !['DELIVERED', 'PICKED_UP', 'CANCELLED', 'FAILED'].includes(o.status)
    }
  }
  const cfg = STATUS_CONFIG[o.orderStatus] || { label: o.orderStatus, tone: 'neutral' }
  // Pickup orders read differently at the end (same labels as the old table).
  const label = o.isPickedUp && o.orderStatus === 'PICKED' ? 'Ready Pickup'
    : o.isPickedUp && o.orderStatus === 'DELIVERED' ? 'Picked Up'
      : cfg.label
  return {
    id: `#${o.orderId}`,
    source: <Tag icon={<ShoppingBagOutlinedIcon />}>RushMyFood</Tag>,
    name: o.user?.name, phone: o.user?.phone, restaurant: o.restaurant?.name,
    items: (o.items || []).map(i => `${i.quantity}x ${i.title}${i.variation?.title ? ` (${i.variation.title})` : ''}`).join(', '),
    total: money(sym, orderTotal(o)),
    payment: paymentLabel(o) || '—',
    type: o.isCatering ? (
      <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
        <FulfilmentTag kind={o.isPickedUp ? 'pickup' : 'delivery'}>{o.isPickedUp ? 'Pickup' : 'Delivery'}</FulfilmentTag>
        <CateringTag />
      </Box>
    ) : <FulfilmentTag kind={o.isPickedUp ? 'pickup' : 'delivery'}>{o.isPickedUp ? 'Pickup' : 'Delivery'}</FulfilmentTag>,
    status: (
      <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
        <StatusChip size="sm" toneName={cfg.tone} label={label} />
        {isRefundedOrder(o) && <StatusChip size="sm" toneName="refunded" label="Refunded" />}
      </Box>
    ),
    open: OPEN(o)
  }
}

const headSx = {
  ...labelSx,
  py: 1.25,
  bgcolor: ops.subtle,
  borderBottom: `1px solid ${ops.border}`,
  whiteSpace: 'nowrap'
}

const bone = w => ({
  width: w, height: 12, borderRadius: '6px',
  background: 'linear-gradient(90deg, #F2F4F7 0px, #FAFAFB 80px, #F2F4F7 160px)',
  backgroundSize: '640px 100%',
  animation: `${shimmer} 1.3s linear infinite`,
  ...noMotion
})

const OrdersTable = ({
  orders,
  loading,
  isSuperAdmin,
  currencySymbol = '$',
  sortBy,
  onSort,
  resetKey,
  onOpen,
  selected,
  updateSelected,
  subscribeToMore,
  empty
}) => {
  const restaurantId = localStorage.getItem('restaurantId')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  // Same place-order subscription the old table ran on the restaurant's list.
  useEffect(() => {
    if (isSuperAdmin || !restaurantId || !subscribeToMore) return
    subscribeToMore({
      document: ORDER_PLACED,
      variables: { restaurant: restaurantId },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev
        const newOrder = subscriptionData.data.subscribePlaceOrder
        if (!newOrder) return prev
        const existingIndex = (prev.ordersByRestId || []).findIndex(o => o._id === newOrder._id)
        if (existingIndex === -1) {
          return { ordersByRestId: [newOrder, ...(prev.ordersByRestId || [])] }
        }
        const updated = [...prev.ordersByRestId]
        updated[existingIndex] = newOrder
        return { ordersByRestId: updated }
      },
      onError: err => console.log('subscription error', err)
    })
  }, []) // run once on mount

  // Keep an open marketplace order's modal in step with fresh data.
  useEffect(() => {
    if (!selected || !updateSelected) return
    const fresh = orders.find(o => !o.__ehungry && !o.__courier && o._id === selected._id)
    if (fresh && fresh !== selected) updateSelected(fresh)
  }, [orders])

  // Back to the first page whenever the filters change.
  useEffect(() => { setPage(0) }, [resetKey])

  const pageRows = orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  // Clicking Date / Total sorts by it; a second click flips the direction.
  const sortHead = (label, desc, asc) => {
    const active = sortBy === desc || sortBy === asc
    return (
      <TableSortLabel
        active={active}
        direction={sortBy === asc ? 'asc' : 'desc'}
        onClick={() => onSort(sortBy === desc ? asc : desc)}
        sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { fontSize: 14 } }}
      >
        {label}
      </TableSortLabel>
    )
  }
  /* Fixed widths so every column fits a laptop screen; long text ellipsises.
     Payment sits under the total rather than in a column of its own. */
  const columns = [
    { key: 'id', label: 'Order', width: isSuperAdmin ? '12%' : '13%' },
    { key: 'customer', label: 'Customer', width: isSuperAdmin ? '17%' : '20%' },
    ...(isSuperAdmin ? [{ key: 'restaurant', label: 'Restaurant', width: '14%' }] : []),
    { key: 'items', label: 'Items', width: isSuperAdmin ? '15%' : '22%' },
    { key: 'total', label: sortHead('Total', 'TOTAL_DESC', 'TOTAL_ASC'), align: 'right', width: '13%' },
    { key: 'type', label: 'Type', width: '10%' },
    { key: 'status', label: 'Status', width: '10%' },
    { key: 'date', label: sortHead('Date', 'NEWEST', 'OLDEST'), width: isSuperAdmin ? '9%' : '12%' }
  ]
  const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

  return (
    <Box sx={{ bgcolor: ops.surface, border: `1px solid ${ops.border}`, borderRadius: RADIUS.card, boxShadow: SHADOW.card, overflow: 'hidden' }}>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 900, '& th, & td': { px: 1.25 }, '& th:first-of-type, & td:first-of-type': { pl: 2 }, '& th:last-of-type, & td:last-of-type': { pr: 2 } }}>
          <TableHead>
            <TableRow>
              {columns.map(c => (
                <TableCell key={c.key} align={c.align || 'left'} sx={{ ...headSx, width: c.width }}>{c.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map(c => (
                    <TableCell key={c.key} sx={{ py: 2, borderBottom: `1px solid ${ops.border}` }}>
                      <Box sx={bone(c.key === 'items' ? 160 : c.key === 'customer' ? 120 : 64)} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ p: 2, borderBottom: 0 }}>{empty}</TableCell>
              </TableRow>
            ) : (
              pageRows.map(o => {
                const r = toRow(o, currencySymbol)
                const key = o.__ehungry ? `eh-${o._id}` : o.__courier ? `em-${o._id}` : o._id
                return (
                  <TableRow
                    key={key}
                    hover
                    tabIndex={0}
                    aria-label={`Open order ${r.id}`}
                    onClick={() => onOpen(o)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(o) } }}
                    sx={{
                      cursor: 'pointer',
                      // Orders still in play get a faint amber tint, as in the old table.
                      bgcolor: r.open ? 'rgba(247,144,9,0.05)' : 'transparent',
                      transition: `background-color 120ms ${EASE}`,
                      '&.MuiTableRow-hover:hover': { bgcolor: ops.subtle },
                      '& td': { borderBottom: `1px solid ${ops.border}`, py: 1.25, verticalAlign: 'middle' },
                      '&:last-of-type td': { borderBottom: 0 },
                      ...focusRing,
                      '&:focus-visible': { outline: `2px solid ${ops.focus}`, outlineOffset: -2 }
                    }}
                  >
                    <TableCell>
                      <Typography title={r.id} sx={{ fontSize: FS.md, fontWeight: 600, color: ops.text, ...ellipsis, ...tabular }}>{r.id}</Typography>
                      <Box sx={{ mt: 0.5, '& > span': { height: 20, fontSize: FS.label } }}>{r.source}</Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                        <InitialsAvatar name={r.name} size={28} />
                        <Box minWidth={0}>
                          <Typography noWrap sx={{ fontSize: FS.sm, fontWeight: 500, color: ops.text }}>{r.name || '—'}</Typography>
                          {r.phone && <Typography noWrap sx={{ fontSize: FS.xs, color: ops.muted, ...tabular }}>{r.phone}</Typography>}
                        </Box>
                      </Box>
                    </TableCell>
                    {isSuperAdmin && (
                      <TableCell>
                        <Typography noWrap title={r.restaurant || ''} sx={{ fontSize: FS.sm, color: ops.text2 }}>{r.restaurant || '—'}</Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography noWrap title={r.items} sx={{ fontSize: FS.sm, color: ops.text2 }}>{r.items || '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontSize: FS.md, fontWeight: 600, color: ops.text, whiteSpace: 'nowrap', ...tabular }}>{r.total}</Typography>
                      <Typography noWrap title={r.payment} sx={{ fontSize: FS.xs, color: ops.muted, ...tabular }}>{r.payment}</Typography>
                    </TableCell>
                    <TableCell sx={{ overflow: 'hidden' }}>{r.type}</TableCell>
                    <TableCell sx={{ overflow: 'hidden' }}>{r.status}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography sx={{ fontSize: FS.sm, color: ops.text, ...ellipsis, ...tabular }}>
                        {dateIn(o.createdAt, orderZone(o))}
                      </Typography>
                      <Typography sx={{ fontSize: FS.xs, color: ops.muted, ...tabular }}>
                        {timeIn(o.createdAt, orderZone(o))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && orders.length > 0 && (
        <TablePagination
          component="div"
          count={orders.length}
          page={Math.min(page, Math.max(0, Math.ceil(orders.length / rowsPerPage) - 1))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            borderTop: `1px solid ${ops.border}`,
            color: ops.text2,
            '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': { color: ops.text2, fontSize: FS.sm, ...tabular },
            '& .MuiTablePagination-select': { color: ops.text2 },
            '& .MuiTablePagination-actions button': { color: ops.text2 }
          }}
        />
      )}
    </Box>
  )
}

export default OrdersTable
